import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiService } from '../../config/api';
import { Helmet } from 'react-helmet-async';
import posthog from 'posthog-js'; // Added PostHog import

function Scan() {
  const { qrId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const pageTitle = qrId ? `Scanning QR: ${qrId} - ScanMaadi` : 'Scan QR Code - ScanMaadi';

  useEffect(() => {
    // Check if the QR code is associated with a vendor
    posthog.capture('public_scan_page_viewed', { qrId });

    const checkQrCode = async () => {
      try {
        setLoading(true);

        // Validate the UUID format
        let validUuid = qrId;
        try {
          // Check if the qrId is a valid UUID format
          // This regex checks for UUID format (8-4-4-4-12 hex digits)
          const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
          if (!uuidPattern.test(qrId)) {
            posthog.capture('public_scan_error_invalid_format_client', { qrId });
            throw new Error('Invalid QR code format');
          }
        } catch (err) {
          setError('Invalid QR code format. Please scan a valid QR code.');
          setLoading(false);
          // Event already captured above
          return;
        }

        posthog.capture('public_scan_qr_check_attempted', { qrId: validUuid });
        const response = await apiService.qrcodes.check(validUuid);
        const { data } = response.data;

        if (data.associated) {
          posthog.capture('public_scan_qr_check_success_associated', { qrId: validUuid, vendorId: data.vendorId });
          navigate(`/vendor/${data.vendorId}`);
        } else {
          posthog.capture('public_scan_qr_check_success_not_associated', { qrId: validUuid });
          navigate(`/admin/qrcode/associate/${validUuid}`);
        }
      } catch (err) {
        console.error('Error checking QR code:', err);
        const errorMessage = err.response?.data?.message || err.message || 'Failed to check QR code. Please try again later.';
        setError(errorMessage);
        posthog.capture('public_scan_qr_check_failed', { qrId, error: errorMessage, status: err.response?.status });
      } finally {
        setLoading(false);
      }
    };

    if (qrId) {
      checkQrCode();
    } else {
      setError('No QR ID provided.');
      setLoading(false);
      posthog.capture('public_scan_error_no_qrid');
    }
  }, [qrId, navigate]);

  if (loading) {
    return (
      <>
        <Helmet>
          <title>{pageTitle}</title>
        </Helmet>
        <div className="flex items-center justify-center min-h-screen p-4">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
          <p className="ml-3">Processing QR code...</p>
        </div>
      </>
    );
  }

  if (error) {
    // Error event already captured in useEffect
    return (
      <>
        <Helmet>
          <title>Error Scanning QR - ScanMaadi</title>
        </Helmet>
        <div className="flex flex-col items-center justify-center min-h-screen p-4">
          <div className="text-red-500 mb-4">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h1 className="text-xl font-bold mb-2">Error</h1>
          <p className="text-center">{error}</p>
           <button
            onClick={() => {
                posthog.capture('public_scan_error_try_again_clicked', { qrId, error });
                window.location.reload();
            }}
            className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700"
          >
            Try Again
          </button>
        </div>
      </>
    );
  }

  return (
    <Helmet>
      <title>{pageTitle}</title>
      <meta name="description" content={`Processing QR code scan for ID: ${qrId}.`} />
    </Helmet>
  );
}

export default Scan;